import { alpha, darken } from "@mui/material/styles";
import { Box, Grid, LinearProgress, Paper, Skeleton, Stack, Tooltip, Typography } from "@mui/material";
import {
  CheckCircleOutlineOutlined,
  CloudOffOutlined,
  ErrorOutlineOutlined,
  WarningAmberOutlined,
} from "@mui/icons-material";
import CardDataComponent from "../../../components/overview/cardDataComponent";

// Estado de carregamento / "sem dados da API" (usado quando a fonte é só a API).
export function EstadoApi({
  styles,
  theme,
  loading = false,
  mensagem = "Sem dados da API",
  detalhe = "Esta seção ainda não é fornecida pela API.",
  altura = 220,
}) {
  return (
    <Paper
      sx={{ ...styles.chartCard, backgroundColor: theme.palette.background.paper }}
      elevation={theme.palette.mode === "dark" ? 0 : 3}
    >
      <Stack
        alignItems="center"
        justifyContent="center"
        gap="12px"
        sx={{ minHeight: altura, py: 4 }}
      >
        {loading ? (
          <>
            <Skeleton
              variant="circular"
              width={42}
              height={42}
              animation="wave"
              sx={{ bgcolor: alpha(theme.palette.text.primary, 0.08) }}
            />
            <Skeleton
              variant="rounded"
              width="52%"
              height={18}
              animation="wave"
              sx={{ bgcolor: alpha(theme.palette.text.primary, 0.08), borderRadius: "6px" }}
            />
            <Skeleton
              variant="rounded"
              width="72%"
              height={13}
              animation="wave"
              sx={{ bgcolor: alpha(theme.palette.text.primary, 0.06), borderRadius: "6px" }}
            />
          </>
        ) : (
          <>
            <CloudOffOutlined
              sx={{ fontSize: 34, color: theme.palette.text.secondary }}
            />
            <Typography sx={{ fontSize: 15, fontWeight: 700 }}>{mensagem}</Typography>
            <Typography
              sx={{ fontSize: 12, color: theme.palette.text.secondary, textAlign: "center" }}
            >
              {detalhe}
            </Typography>
          </>
        )}
      </Stack>
    </Paper>
  );
}

export function EmptyChartState({
  theme,
  title = "Sem dados",
  detail = "Não foram encontrados dados para este gráfico no período selecionado.",
  altura = 286,
}) {
  return (
    <Stack
      alignItems="center"
      justifyContent="center"
      gap="10px"
      sx={{
        minHeight: altura,
        px: 2,
        py: 4,
        borderRadius: "12px",
        border: `1px dashed ${alpha(theme.palette.text.primary, 0.16)}`,
        backgroundColor: alpha(theme.palette.text.primary, 0.025),
        textAlign: "center",
      }}
    >
      <Box
        sx={{
          width: 54,
          height: 54,
          borderRadius: "14px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: theme.palette.text.secondary,
          backgroundColor: alpha(theme.palette.primary.main, 0.1),
        }}
      >
        <CloudOffOutlined sx={{ fontSize: 30 }} />
      </Box>
      <Typography sx={{ fontSize: 15, fontWeight: 800, color: theme.palette.text.primary }}>
        {title}
      </Typography>
      <Typography
        sx={{
          maxWidth: 360,
          fontSize: 12.5,
          lineHeight: 1.45,
          color: theme.palette.text.secondary,
        }}
      >
        {detail}
      </Typography>
    </Stack>
  );
}

// Skeleton genérico no tamanho/formato do card: um único retângulo arredondado.
// minHeight: com height="100%" o skeleton estica até a altura do vizinho de linha e nunca fica menor que isso.
export function CardSkeleton({ theme, height = 286, minHeight, radius = "16px" }) {
  return (
    <Skeleton
      variant="rounded"
      width="100%"
      height={height}
      animation="wave"
      sx={{ bgcolor: alpha(theme.palette.text.primary, 0.08), borderRadius: radius, minHeight }}
    />
  );
}

// Skeleton da aba Resumo, na ordem e largura reais: 5 KPIs, linha do seletor de visualização, 3 cards de largura
// total (por mês, evolução, regional) e os 2 de motivos lado a lado. Alturas são aproximadas.
const SLOTS_RESUMO = [
  { cols: "full", height: 44 },
  { cols: "full", height: 300 },
  { cols: "full", height: 410 },
  { cols: "full", height: 420 },
  { cols: "half", height: 330 },
  { cols: "half", height: 330 },
];
const SKELETON_COLS = {
  full: { xs: 2, sm: 4, md: 6, lg: 10 },
  half: { xs: 2, sm: 4, md: 6, lg: 5 },
};

export function ReportTabSkeleton({
  theme,
  columns = { xs: 2, sm: 4, md: 6, lg: 10, xl: 10 },
  metric = { xs: 2, sm: 2, md: 2, lg: 2 },
  metrics = 5,
  slots = SLOTS_RESUMO,
}) {
  // Cada slot é um retângulo no tamanho/formato do card.
  return (
    <Grid container spacing={1} columns={columns} sx={{ maxWidth: "100%", minWidth: 0 }}>
      {Array.from({ length: metrics }).map((_, idx) => (
        <Grid item key={`metric-${idx}`} {...metric}>
          <CardSkeleton theme={theme} height={110} />
        </Grid>
      ))}
      {slots.map((slot, idx) => (
        <Grid item key={`slot-${idx}`} {...SKELETON_COLS[slot.cols]}>
          <CardSkeleton theme={theme} height={slot.height} />
        </Grid>
      ))}
    </Grid>
  );
}

/* ----------------------------- formatters ----------------------------- */
export const formatInt = (value) => Number(value || 0).toLocaleString("pt-BR");

export const formatPct = (value) => `${(Number(value || 0) * 100).toFixed(1)}%`;

export const formatTempo = (segundos) => {
  const total = Number(segundos || 0);
  const dias = Math.floor(total / 86400);
  const horas = Math.floor((total % 86400) / 3600);
  const minutos = Math.floor((total % 3600) / 60);
  const partes = [];
  if (dias) partes.push(`${dias}d`);
  if (horas) partes.push(`${horas}h`);
  partes.push(`${minutos}m`);
  return partes.join(" ");
};

/* ----------------------------- semáforo ----------------------------- */
// Cores do semáforo pela paleta do tenant, com fallback no MUI se o config.js não tiver o token.
export const semaforoCores = (theme) => ({
  ok: theme.palette.cardGraphic?.graphColor?.green || theme.palette.success.main,
  atencao: theme.palette.cardGraphic?.graphColor?.orange || theme.palette.warning.main,
  critico: theme.palette.colors?.red?.medium || theme.palette.error.main,
});

// Faixa do semáforo: crítico a partir de 30%, atenção de 20% a 30%, ok abaixo de 20%.
export const LIMITE_CRITICO = 0.3;
export const pctFaixa = (pct) => {
  const p = Number(pct || 0);
  if (p >= LIMITE_CRITICO) return "critico";
  if (p >= 0.2) return "atencao";
  return "ok";
};

export const pctColor = (theme, pct) => semaforoCores(theme)[pctFaixa(pct)];

// Chave de cardTrafficData (cards KPI da casa) para cada faixa do semáforo.
export const FAIXA_CARD = { ok: "statusUp", atencao: "outputData", critico: "statusDown" };

/* ----------------------------- gráficos ----------------------------- */
// Grid horizontal tracejado, igual aos gráficos da dashAtendimentos.
export const chartGridProps = (theme) => ({
  vertical: false,
  stroke: theme.palette.divider,
  strokeDasharray: "3",
});

// Ticks 10px em text.primary, sem linha de eixo.
export const chartAxisProps = (theme) => ({
  tick: { fontSize: 10, fill: theme.palette.text.primary },
  axisLine: false,
  tickLine: false,
});

// Margem esquerda negativa puxa o eixo Y para dentro do card.
export const CHART_MARGIN = { top: 8, right: 8, left: -20, bottom: 0 };

// Rampa de calor do laranja do tenant, de "menos" a "mais". No claro, mais forte = mais escuro (5 degraus);
// no escuro, mais forte = mais claro (4 degraus). Texto branco/preto em cada degrau tem 4,9:1 ou mais nos dois modos.
export const rampaCalor = (theme) => {
  const { light, medium, dark, darker } = theme.palette.colors.orange;
  return theme.palette.mode === "dark"
    ? [darker, dark, medium, light]
    : [light, medium, dark, darken(dark, 0.2), darker];
};

// Marcador da série no tooltip: pílula (barra) ou traço com ponto (linha), na cor da série.
const MarcadorSerie = ({ cor, forma }) =>
  forma === "linha" ? (
    <Box
      component="span"
      sx={{
        position: "relative",
        width: 14,
        height: 2,
        flexShrink: 0,
        backgroundColor: cor,
        "&::after": {
          content: '""',
          position: "absolute",
          left: 4,
          top: -2,
          width: 6,
          height: 6,
          borderRadius: "50%",
          backgroundColor: cor,
        },
      }}
    />
  ) : (
    <Box component="span" sx={{ width: 4, height: 12, flexShrink: 0, borderRadius: 999, backgroundColor: cor }} />
  );

// Tooltip dos gráficos com marcador na cor da série; o texto fica neutro por contraste.
// itens: [{ nome, valor, cor, forma?: "barra" | "linha" }]
export function TooltipSeries({ styles, label, itens }) {
  return (
    <Box sx={styles.chartTooltip}>
      <Box sx={styles.chartTooltipLabel}>{label}</Box>
      {itens.map((i) => (
        <Box
          key={i.nome}
          sx={{ ...styles.chartTooltipItem, display: "flex", alignItems: "center", gap: "6px" }}
        >
          <MarcadorSerie cor={i.cor} forma={i.forma} />
          {i.nome}: {i.valor}
        </Box>
      ))}
    </Box>
  );
}

/* ----------------------------- table styles ----------------------------- */
export const tableHeadCell = (theme, align) => ({
  textAlign: align,
  padding: "10px 12px",
  fontSize: 11,
  fontWeight: 800,
  textTransform: "uppercase",
  color: theme.palette.text.secondary,
  borderBottom: `1px solid ${alpha(theme.palette.text.primary, 0.12)}`,
  whiteSpace: "nowrap",
});

export const tableBodyCell = (theme, align, idx) => ({
  textAlign: align,
  padding: "10px 12px",
  fontSize: 13,
  color: theme.palette.text.primary,
  minWidth: 0,
  borderBottom: `1px solid ${alpha(theme.palette.text.primary, 0.06)}`,
  backgroundColor:
    idx % 2 === 1 ? alpha(theme.palette.text.primary, 0.02) : "transparent",
  whiteSpace: "nowrap",
});

// Realce da linha ao passar o mouse nas tabelas da tela: primária translúcida, visível sobre a linha zebrada e a lisa.
export const rowHoverBg = (theme) => alpha(theme.palette.primary.main, 0.12);
// Para <tr> das tabelas HTML (as células têm fundo próprio, então o realce vai nelas).
export const trHover = (theme) => ({ "&:hover > td": { backgroundColor: rowHoverBg(theme) } });

// Para tabelas do CustomTableComponent (ele neutraliza o hover da linha): vale no elemento que envolve a tabela.
export const tableHoverSx = (theme) => ({
  "& .MuiTableBody-root .MuiTableRow-root:hover": { backgroundColor: rowHoverBg(theme) },
});

// Mapas de calor: estrutura comum (altura, cantos, tipografia, cabeçalho e rótulo de linha).
// Cada mapa decide só a cor da célula e o conteúdo; a escala/legenda é do próprio mapa.
export const heatVazioBg = (theme) => alpha(theme.palette.text.primary, 0.04);

export const heatHeadSx = (theme) => ({
  fontSize: 11,
  fontWeight: 700,
  lineHeight: 1.2,
  color: theme.palette.text.secondary,
  textAlign: "center",
  pb: "4px",
});

// Sem display: serve tanto em <td> quanto em grid (no grid, some display:flex e alignItems:center).
export const heatRowLabelSx = (theme) => ({
  fontSize: 12,
  fontWeight: 700,
  color: theme.palette.text.primary,
  whiteSpace: "nowrap",
});

// Célula do mapa: valor em destaque e, opcionalmente, uma linha de apoio e um tooltip.
// Sem bg, usa o neutro de "vazio/zero".
export function HeatCell({ theme, bg, color, valor, apoio, title, opacity }) {
  const cell = (
    <Box
      sx={{
        minHeight: 40,
        borderRadius: "4px",
        backgroundColor: bg ?? heatVazioBg(theme),
        opacity,
        px: "6px",
        py: "4px",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
      }}
    >
      <Typography sx={{ fontSize: 13, fontWeight: 800, lineHeight: 1.1, color: color || theme.palette.text.primary }}>
        {valor}
      </Typography>
      {apoio && (
        <Typography sx={{ fontSize: 11, lineHeight: 1.3, color: theme.palette.text.secondary }}>{apoio}</Typography>
      )}
    </Box>
  );
  return title ? (
    <Tooltip title={title} arrow placement="top">
      {cell}
    </Tooltip>
  ) : (
    cell
  );
}

/* ----------------------------- components ----------------------------- */
// helperTitle: texto completo mostrado em tooltip quando o helper vem abreviado (nomes longos).
// atualizando: já há dado na tela e ele está sendo recarregado; mostra só a barra de progresso (o skeleton é do 1º carregamento).
export function MetricCard({ theme, icon: Icon, title, value, helper, helperTitle, loading = false, atualizando = false, faixa = null }) {
  if (loading) {
    return <CardSkeleton theme={theme} height={110} />;
  }

  // Sem faixa de semáforo o card fica no azul neutro da casa (inputData).
  const colorCard = faixa ? FAIXA_CARD[faixa] : "inputData";

  return (
    <CardDataComponent
      cardTile={title}
      cardValue={value}
      helperText={
        helperTitle ? (
          <Tooltip title={helperTitle} arrow placement="top">
            <span>{helper}</span>
          </Tooltip>
        ) : (
          helper
        )
      }
      icon={Icon}
      colorCard={colorCard}
      loading={atualizando}
    />
  );
}

// Legenda das faixas de reincidência (semáforo). Usa ícone DIFERENTE por faixa
// para não depender só da cor (acessível a daltônicos).
export function SemaforoLegend({ theme, sx }) {
  const sem = semaforoCores(theme);
  const bands = [
    { icon: CheckCircleOutlineOutlined, color: sem.ok, label: "OK", range: "< 20%" },
    { icon: WarningAmberOutlined, color: sem.atencao, label: "Atenção", range: "20–30%" },
    { icon: ErrorOutlineOutlined, color: sem.critico, label: "Crítico", range: "≥ 30%" },
  ];
  return (
    <Stack
      direction="row"
      flexWrap="wrap"
      alignItems="center"
      sx={{ gap: "8px 18px", justifyContent: "center", ...sx }}
    >
      <Typography
        sx={{
          fontSize: 11,
          fontWeight: 800,
          textTransform: "uppercase",
          letterSpacing: "0.04em",
          lineHeight: 1,
          color: theme.palette.text.secondary,
        }}
      >
        Faixas de reincidência
      </Typography>
      {bands.map((b) => (
        <Stack key={b.label} direction="row" alignItems="center" gap="6px">
          <b.icon sx={{ fontSize: 18, color: b.color, display: "block" }} />
          <Typography sx={{ fontSize: 12.5, fontWeight: 700, lineHeight: 1, color: b.color }}>
            {b.label}
          </Typography>
          <Typography sx={{ fontSize: 12, fontWeight: 600, lineHeight: 1, color: theme.palette.text.secondary }}>
            {b.range}
          </Typography>
        </Stack>
      ))}
    </Stack>
  );
}

export function SectionTitle({ theme, title, helper, icon: Icon }) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: "10px",
        mt: "8px",
        mb: "2px",
        pb: "6px",
        borderBottom: `2px solid ${alpha(theme.palette.text.primary, 0.08)}`,
      }}
    >
      {Icon && (
        <Box
          sx={{
            width: 30,
            height: 30,
            borderRadius: "9px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: alpha(theme.palette.primary.main, 0.12),
            color: theme.palette.primary.main,
          }}
        >
          <Icon sx={{ fontSize: 18 }} />
        </Box>
      )}
      <Box>
        <Typography
          sx={{
            fontSize: 13,
            fontWeight: 800,
            textTransform: "uppercase",
            letterSpacing: "0.04em",
            color: theme.palette.text.primary,
          }}
        >
          {title}
        </Typography>
        {helper && (
          <Typography sx={{ fontSize: 12, color: theme.palette.text.secondary }}>
            {helper}
          </Typography>
        )}
      </Box>
    </Box>
  );
}

export function ChartCard({
  styles,
  theme,
  title,
  subtitle,
  topRight,
  children,
  loading = false,
  atualizando = false,
  noData = false,
  noDataTitle = "Sem dados",
  noDataMessage = "Não foram encontrados dados para este gráfico no período selecionado.",
  emptyHeight,
  sx,
}) {
  // Carregando: um único retângulo no tamanho/formato do card (sem cabeçalho). Cards lado a lado
  // ganham a mesma altura: o menor estica até o vizinho.
  if (loading) {
    return <CardSkeleton theme={theme} height="100%" minHeight={(emptyHeight || 286) + 88} />;
  }

  return (
    <Paper
      sx={{ ...styles.chartCard, backgroundColor: theme.palette.background.paper, ...sx }}
      elevation={theme.palette.mode === "dark" ? 0 : 3}
    >
      {atualizando && <LinearProgress sx={styles.linearProgress} />}
      <Box sx={styles.cardHeader}>
        <Box>
          <Typography sx={styles.cardTitle}>{title}</Typography>
          {subtitle && <Typography sx={styles.cardSubtitle}>{subtitle}</Typography>}
        </Box>
        {topRight}
      </Box>
      {noData ? (
        <EmptyChartState
          theme={theme}
          title={noDataTitle}
          detail={noDataMessage}
          altura={emptyHeight || 286}
        />
      ) : (
        children
      )}
    </Paper>
  );
}
